/** What the pipeline is doing, what it has done, and what it gave up on. */

import { Loading, formatDuration, relativeTime } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { SectionHeader } from '../components'
import { usePipelineRuns, usePipelineStatus, useRetryArchived } from '../hooks'

const QUEUE_MEANING: Record<string, string> = {
  extract: 'source rows waiting to be read',
  resolve: 'records waiting to be matched and merged',
  graph: 'relationships waiting to be linked',
  index: 'records waiting to be made searchable',
}

export function PipelinePanel({ graphId }: { graphId: string }) {
  const status = usePipelineStatus(graphId)
  const runs = usePipelineRuns(graphId)
  const retry = useRetryArchived(graphId)
  const toast = useToast()

  if (status.isLoading) return <Loading label="Loading pipeline status…" />

  const queues = status.data?.queues ?? {}
  const archived = status.data?.archived ?? []

  return (
    <div className="stack" style={{ marginTop: 8 }}>
      <SectionHeader
        title="Pipeline"
        hint="Four passes in order: read the source, resolve the record, link the relationships, make it searchable. Each one is safe to re-run."
      />

      <div className="queuepills">
        {Object.entries(queues).map(([queue, depth]) => (
          <span key={queue} className="queuepill" data-busy={depth > 0} title={QUEUE_MEANING[queue]}>
            {queue} <strong>{depth}</strong>
          </span>
        ))}
      </div>

      <div className="row" style={{ gap: 18 }}>
        <div>
          <div className="small faint">Staged rows</div>
          <div className="mono small">
            {Object.entries(status.data?.staging ?? {})
              .map(([k, v]) => `${k.toLowerCase()} ${v}`)
              .join(' · ') || 'none'}
          </div>
        </div>
        <div>
          <div className="small faint">Extracted records</div>
          <div className="mono small">
            {Object.entries(status.data?.extracted ?? {})
              .map(([k, v]) => `${k.toLowerCase()} ${v}`)
              .join(' · ') || 'none'}
          </div>
        </div>
      </div>

      {archived.length > 0 && (
        <div className="callout callout-danger">
          <p className="callout-title">
            {archived.length} message{archived.length === 1 ? '' : 's'} gave up after repeated
            failures
          </p>
          {archived.slice(0, 5).map((message) => (
            <p key={message.id} className="small">
              <span className="mono">{message.queue}</span> — {message.error}
            </p>
          ))}
          <button
            className="btn btn-sm"
            style={{ marginTop: 8 }}
            disabled={retry.isPending}
            onClick={async () => {
              try {
                const result = await retry.mutateAsync(undefined)
                toast.success(`Requeued ${result.requeued}.`)
              } catch (error) {
                toast.error(error)
              }
            }}
          >
            {retry.isPending ? 'Requeuing…' : 'Try them again'}
          </button>
        </div>
      )}

      {(runs.data?.length ?? 0) > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Run</th>
                <th>Trigger</th>
                <th>Handled</th>
                <th>Produced</th>
                <th>Took</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.data!.slice(0, 10).map((run) => {
                const handled = Object.values(run.counts).reduce((s, c) => s + c.handled, 0)
                const produced = Object.values(run.counts).reduce((s, c) => s + c.produced, 0)
                return (
                  <tr key={run.run_id}>
                    <td className="muted small">{relativeTime(run.at)}</td>
                    <td>
                      <span className="badge badge-neutral">{run.trigger}</span>
                    </td>
                    <td>{handled}</td>
                    <td>{produced}</td>
                    <td className="mono small">{formatDuration(run.duration_ms)}</td>
                    <td>
                      <span
                        className={`badge ${run.status === 'ok' ? 'badge-ok' : 'badge-danger'}`}
                        title={run.error ?? undefined}
                      >
                        {run.status}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
